import type { ContentfulStatusCode } from "hono/utils/http-status";
import { z } from "zod";

const commonFields = {
  message: z.string(),
  display: z.boolean(),
};

// A kind with no shape here has none on the wire, and is reported as an InternalServerError.
const clientErrorSchemas = {
  FormValidationError: z.object({
    kind: z.literal("FormValidationError"),
    formErrors: z.record(z.string(), z.string()),
    ...commonFields,
  }),
  ValidationError: z.object({
    kind: z.literal("ValidationError"),
    ...commonFields,
  }),
  NotFoundError: z.object({
    kind: z.literal("NotFoundError"),
    ...commonFields,
  }),
  AuthorisationError: z.object({
    kind: z.literal("AuthorisationError"),
    ...commonFields,
  }),
  ForbiddenError: z.object({
    kind: z.literal("ForbiddenError"),
    ...commonFields,
  }),
  ConflictError: z.object({
    kind: z.literal("ConflictError"),
    ...commonFields,
  }),
  TooManyRequestsError: z.object({
    kind: z.literal("TooManyRequestsError"),
    ...commonFields,
  }),
  InternalServerError: z.object({
    kind: z.literal("InternalServerError"),
    context: z.record(z.string(), z.unknown()).optional(),
    ...commonFields,
  }),
};

export type ClientErrorKind = keyof typeof clientErrorSchemas;

export const ClientErrorSchema = z.discriminatedUnion("kind", [
  clientErrorSchemas.FormValidationError,
  clientErrorSchemas.ValidationError,
  clientErrorSchemas.NotFoundError,
  clientErrorSchemas.AuthorisationError,
  clientErrorSchemas.ForbiddenError,
  clientErrorSchemas.ConflictError,
  clientErrorSchemas.TooManyRequestsError,
  clientErrorSchemas.InternalServerError,
]);
export type ClientErrorPayload = z.infer<typeof ClientErrorSchema>;

/** Declared here rather than with the classes, so `ErrorKind` needs no import from the services. */
export type UpstreamErrorKind =
  | "UpstreamRateLimitError"
  | "UpstreamOverloadError"
  | "UpstreamMalformedResponseError"
  | "UpstreamApiError";

/** Kinds with no client representation of their own, reported as an `InternalServerError`. */
export type ErrorKind = ClientErrorKind | UpstreamErrorKind;

// Not captured by Sentry
const IGNORED_ERRORS: ClientErrorKind[] = [
  "NotFoundError",
  "ValidationError",
  "AuthorisationError",
  "ForbiddenError",
  "ConflictError",
  "TooManyRequestsError",
];

/** Reported to the user rather than only logged. */
const DISPLAYED: ClientErrorKind[] = [
  "FormValidationError",
  "ValidationError",
  "NotFoundError",
  "AuthorisationError",
  "ConflictError",
  "TooManyRequestsError",
  "InternalServerError",
];

// What each kind means over HTTP. A kind carries no status of its own — every consumer maps it, and
// this is the map for the one that answers requests.
const STATUS_BY_KIND: Record<ClientErrorKind, ContentfulStatusCode> = {
  FormValidationError: 400,
  ValidationError: 400,
  NotFoundError: 404,
  AuthorisationError: 401,
  ForbiddenError: 403,
  ConflictError: 409,
  TooManyRequestsError: 429,
  InternalServerError: 500,
};

const GENERIC_MESSAGE = "Something went wrong, please try again later";

export type RetryHint = { after?: Date };

export type AppErrorOptions = { cause?: unknown; retry?: RetryHint | false };

export abstract class AppError extends Error {
  abstract readonly kind: ErrorKind;

  /**
   * Present means a retry can succeed regardless of kind; `after` is the earliest it should run.
   * `false` means a retry must not run regardless of kind: the failed call may still have had its
   * effect, so running it again would repeat that effect.
   */
  readonly retry?: RetryHint | false;

  constructor(message: string, options?: AppErrorOptions) {
    super(message, options);
    this.name = new.target.name;
    this.retry = options?.retry;
    // Without this the stack opens on the constructors rather than on the code that raised it.
    Error.captureStackTrace?.(this, new.target);
  }

  /** Finer than `kind` where a subclass can say more, so failures sharing a kind stay distinct. */
  get code(): string {
    return this.kind;
  }

  /** Fields this kind puts on the wire beyond the common ones. */
  protected get clientFields(): Record<string, unknown> {
    return {};
  }

  private get presentAs(): ClientErrorKind {
    return this.kind in clientErrorSchemas ? (this.kind as ClientErrorKind) : "InternalServerError";
  }

  get status(): ContentfulStatusCode {
    return STATUS_BY_KIND[this.presentAs];
  }

  get capture(): boolean {
    return !IGNORED_ERRORS.includes(this.presentAs);
  }

  get payload(): ClientErrorPayload {
    const { presentAs } = this;
    // An error reported as another kind must not carry its own message: it describes internals.
    const ownMessage = presentAs === this.kind && this.kind !== "InternalServerError";
    return {
      kind: presentAs,
      message: ownMessage ? this.message : GENERIC_MESSAGE,
      display: DISPLAYED.includes(presentAs),
      ...this.clientFields,
    } as ClientErrorPayload;
  }
}

// Represents failing to parse a form
export class FormValidationError extends AppError {
  readonly kind = "FormValidationError";
  readonly formErrors: Record<string, string>;
  readonly input?: unknown;
  readonly target?: string;

  constructor({
    message,
    formErrors,
    input,
    target,
  }: {
    message?: string;
    formErrors?: Record<string, string>;
    input?: unknown;
    target?: string;
  }) {
    super(message ?? "Failed to validate");
    this.formErrors = formErrors ?? {};
    this.input = input;
    this.target = target;
  }

  protected get clientFields() {
    return { formErrors: this.formErrors };
  }
}

// Represents failed logic validation i.e user already exists
export class ValidationError extends AppError {
  readonly kind = "ValidationError";

  constructor(message?: string, options?: AppErrorOptions) {
    super(message ?? "Failed to validate", options);
  }
}

export class NotFoundError extends AppError {
  readonly kind = "NotFoundError";

  constructor(message?: string, options?: AppErrorOptions) {
    super(message ?? "Resource not found", options);
  }
}

export class AuthorisationError extends AppError {
  readonly kind = "AuthorisationError";

  constructor(message?: string, options?: AppErrorOptions) {
    super(message ?? "Unauthorized", options);
  }
}

export class ForbiddenError extends AppError {
  readonly kind = "ForbiddenError";

  constructor(message?: string, options?: AppErrorOptions) {
    super(message ?? "Forbidden", options);
  }
}

export class ConflictError extends AppError {
  readonly kind = "ConflictError";

  constructor(message?: string, options?: AppErrorOptions) {
    super(message ?? "Conflict", options);
  }
}

export class TooManyRequestsError extends AppError {
  readonly kind = "TooManyRequestsError";

  constructor(retryAfterSeconds: number, options?: Omit<AppErrorOptions, "retry">) {
    const minutes = Math.max(1, Math.ceil(retryAfterSeconds / 60));
    super(`Too many attempts. Try again in ${minutes} minute${minutes === 1 ? "" : "s"}.`, {
      ...options,
      retry: { after: new Date(Date.now() + retryAfterSeconds * 1000) },
    });
  }
}

export class InternalServerError extends AppError {
  readonly kind = "InternalServerError";

  constructor(
    message?: string,
    readonly context?: Record<string, unknown>,
    options?: AppErrorOptions,
  ) {
    super(message ?? "An unexpected error occurred", options);
  }

  protected get clientFields() {
    return { context: this.context };
  }
}
